import { CBSocketClient, SocketClient } from "cbcore-ts"
import { IF, NO, UIButton, UIColor, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHCore } from "../Core/RHCore"
import { RHUserPasswordPolicy } from "../Core/RHUserPasswordPolicy"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    RHCompanyAdministrationViewTokens,
    RHForgotPasswordViewTokens,
    RHLoginViewTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { LoginViewController } from "./LoginViewController"
import { RHUserName } from "../Core/RHUserName"
import { RHUserNameInputView } from "../Custom components/RH/RHUserNameInputView"


// What a failed socket call leaves in errorResult: the framework's wrapped message, a bare text or an error.
type RHSocketErrorResult =
    | string
    | { message?: string }
    | { _isCBSocketErrorMessage: boolean, messageData?: string | { message?: string } }
    | undefined


export class ResetPasswordViewController extends UIViewController {
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Uus salasõna",
        backgroundColor: UIColor.transparentColor,
        textColor: RHColors.textPrimary,
        textAlignment: UITextView.textAlignment.center,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeXXLarge,
            fontWeight: RHForgotPasswordViewTokens.titleFontWeight,
            lineHeight: RHFont.lineHeightHeading,
            textAlign: "center"
        }
    }).addedAsSubviewToView(this.view)
    
    readonly statusBannerView = new RHAlertBannerView().addedAsSubviewToView(this.view)

    readonly invitationPreviewBannerView = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Success
    }).addedAsSubviewToView(this.view)

    readonly nameInputView = new RHUserNameInputView().configuredWithObject({
        lastNameTextField: {
            controlEventTargetAccumulator: {
                EnterDown: () => this.newPasswordTextField.focus()
            }
        }
    })
    
    readonly newPasswordTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Uus salasõna",
        type: "password",
        autocompleteEnabled: NO,
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.confirmPasswordTextField.focus()
            }
        }
    })
    
    readonly confirmPasswordTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Korda salasõna",
        type: "password",
        autocompleteEnabled: NO,
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.submitButton.sendControlEventForKey(UIButton.controlEvent.EnterDown, event)
            }
        }
    })
    
    readonly submitButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Salvesta uus salasõna" },
        variant: "success",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {
                await this._submitNewPassword()
            }
        }
    })
    
    readonly backToLoginButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Tagasi sisselogimise lehele" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(ResetPasswordViewController.routeComponentName)
                    .routeWithViewControllerComponent(LoginViewController, {})
                    .apply()
            }
        }
    })
    
    readonly bottomView = new UIView().addedAsSubviewToView(this.view)
    
    override _focusManager = new RHFocusManager(() => [
        ...IF(this.view.hasSubview(this.nameInputView))(() => [
            this.nameInputView.firstNameTextField,
            this.nameInputView.lastNameTextField
        ]).ELSE(() => []),
        this.newPasswordTextField,
        this.confirmPasswordTextField,
        this.submitButton,
        this.backToLoginButton
    ])
    
    _resetToken = ""
    _invitationToken = ""
    
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })
        
    }
    
    
    static override readonly routeComponentName = "reset-password"
    
    static override readonly ParameterIdentifierName = {
        token: "token",
        invitationToken: "invitationToken"
    }

    static readonly _invalidInvitationMessage = "Kutse on aegunud, juba kasutatud või link on vigane."
    static readonly _technicalErrorMessage = "Tehniline tõrge. Kui viga püsib, võta ühendust aadressil info@rhr.ee."

    
    override async viewWillAppear() {
        
        await super.viewWillAppear()
        
        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)
        
    }
    
    
    override async viewWillDisappear() {
        
        await super.viewWillDisappear()
        
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        
    }
    
    
    override async handleRoute(route: UIRoute) {
        
        await super.handleRoute(route)
        
        const component = route.componentWithName(ResetPasswordViewController.routeComponentName)
        const previousInvitationToken = this._invitationToken
        this._resetToken = component?.parameters[ResetPasswordViewController.ParameterIdentifierName.token] ?? ""
        this._invitationToken = component?.parameters[
            ResetPasswordViewController.ParameterIdentifierName.invitationToken
        ] ?? ""

        if (previousInvitationToken !== this._invitationToken) {
            this.nameInputView.nameComponents = { firstName: "", lastName: "" }
        }
        if (this._invitationToken) {
            this.nameInputView.addedAsSubviewToView(this.view)
        }
        else {
            this.nameInputView.removeFromSuperview()
        }

        if (
            (!this._resetToken && !this._invitationToken) ||
            (this._resetToken && this._invitationToken)
        ) {
            this.invitationPreviewBannerView.text = ""
            this.statusBannerView.configureWithObject({
                text: "Vigane või puuduv link. Palun taotle uus link.",
                alertType: RHAlertBannerView.type.Error
            })
            this.submitButton.enabled = NO
            this.view.setNeedsLayoutUpToRootView()
            return
        }

        this.invitationPreviewBannerView.text = ""
        this.statusBannerView.text = ""
        this.submitButton.enabled = YES
        if (this._invitationToken) {
            this.submitButton.enabled = NO
        }
        this.view.setNeedsLayoutUpToRootView()

        if (this._invitationToken) {
            await this._loadInvitationPreview(this._resetToken, this._invitationToken)
        }
        
    }


    async _loadInvitationPreview(resetToken: string, invitationToken: string) {

        try {
            const response = await SocketClient.RetrieveUserInvitationPreview(
                { token: invitationToken },
                CBSocketClient.completionPolicy.firstOnly
            )

            if (!this._activeRouteMatchesTokens(resetToken, invitationToken)) {
                return
            }

            if (!response.result) {
                const message = ResetPasswordViewController._messageFromSocketErrorResult(response.errorResult)
                if (message === ResetPasswordViewController._invalidInvitationMessage) {
                    this._showInvalidInvitationError()
                }
                else {
                    this._showInvitationPreviewLoadError()
                }
                return
            }

            const companyName = RHTextSanitizer.escapedHTML(response.result.companyName)
            const roleNames = response.result.roles.map(role =>
                RHCompanyAdministrationViewTokens.roleDisplayLabels[role]
            ).map(roleName =>
                RHTextSanitizer.escapedHTML(roleName)
            ).join(", ")

            this.invitationPreviewBannerView.configureWithObject({
                text:
                    `Sind on kutsutud liituma RHR-is ettevõttega <strong>„${companyName}“</strong>.<br><br>` +
                    `<strong>Ettevõte:</strong> ${companyName}<br><br>` +
                    `<strong>Roll:</strong> ${roleNames}<br><br>` +
                    "Sisesta eesnimi ja perekonnanimi, määra uus salasõna ning logi seejärel keskkonda sisse.",
                alertType: RHAlertBannerView.type.Success
            })
            this.submitButton.enabled = YES
            this.view.setNeedsLayoutUpToRootView()
        }
        catch {
            if (!this._activeRouteMatchesTokens(resetToken, invitationToken)) {
                return
            }
            this._showInvitationPreviewLoadError()
        }

    }


    // Unwraps the framework's _isCBSocketErrorMessage envelope when present.
    static _messageFromSocketErrorResult(errorResult: RHSocketErrorResult): string | undefined {

        const messageData = errorResult && typeof errorResult === "object" && "_isCBSocketErrorMessage" in errorResult && errorResult._isCBSocketErrorMessage
            ? errorResult.messageData
            : errorResult

        if (typeof messageData === "string") {
            return messageData
        }

        if (messageData && "message" in messageData) {
            return messageData.message
        }

        return undefined

    }


    _showInvalidInvitationError() {

        this.invitationPreviewBannerView.text = ""
        this._showError("Kutse on aegunud, juba kasutatud või vigane. Palu saata uus kutse.")
        this.submitButton.enabled = NO

    }


    _showInvitationPreviewLoadError() {

        this.invitationPreviewBannerView.text = ""
        this._showError("Kutse andmete laadimine ebaõnnestus. Palun proovi hiljem uuesti.")
        this.submitButton.enabled = NO

    }
    
    
    async _submitNewPassword() {

        const resetToken = this._resetToken
        const invitationToken = this._invitationToken
        const isInvitationAcceptance = invitationToken.length > 0
        const nameComponents = this.nameInputView.nameComponents
        if (isInvitationAcceptance) {
            const nameError = RHUserName.failedRequirementMessageForComponents(nameComponents)
            if (nameError) {
                this._showError(nameError)
                return
            }
        }
        
        const newPassword = this.newPasswordTextField.text
        const confirmPassword = this.confirmPasswordTextField.text
        
        if (!newPassword) {
            this._showError("Palun sisesta uus salasõna.")
            return
        }
        
        if (!RHUserPasswordPolicy.passwordMeetsMinimumLength(newPassword)) {
            CBDialogViewShower.alert(RHUserPasswordPolicy.minimumLengthRequirementMessage())
            return
        }

        if (!RHUserPasswordPolicy.passwordMeetsMaximumLength(newPassword)) {
            CBDialogViewShower.alert(RHUserPasswordPolicy.maximumLengthRequirementMessage())
            return
        }

        if (!RHUserPasswordPolicy.passwordAvoidsCommonPatterns(newPassword)) {
            CBDialogViewShower.alert(RHUserPasswordPolicy.commonPatternRequirementMessage())
            return
        }

        if (newPassword !== confirmPassword) {
            this._showError("Salasõnad ei kattu.")
            return
        }
        
        this.submitButton.enabled = NO

        try {

            const response = await IF(isInvitationAcceptance)(() =>
                SocketClient.AcceptUserInvitation(
                    { token: invitationToken, newPassword, ...nameComponents },
                    CBSocketClient.completionPolicy.firstOnly
                )
            ).ELSE(() =>
                SocketClient.ResetPassword(
                    { token: resetToken, newPassword },
                    CBSocketClient.completionPolicy.firstOnly
                )
            )

            if (!this._activeRouteMatchesTokens(resetToken, invitationToken)) {
                return
            }

            if (response.result) {

                let loginRoute = UIRoute.currentRoute
                    .routeByRemovingComponentNamed(ResetPasswordViewController.routeComponentName)
                    .routeByRemovingParameterInComponent("settings", "mode", YES)
                    .routeWithViewControllerComponent(LoginViewController, {})

                if (response.result.email) {
                    loginRoute = loginRoute.routeBySettingParameterInComponent(
                        LoginViewController.routeComponentName,
                        LoginViewController.ParameterIdentifierName.email,
                        response.result.email
                    )
                }

                if (isInvitationAcceptance) {
                    loginRoute = loginRoute.routeBySettingParameterInComponent(
                        LoginViewController.routeComponentName,
                        LoginViewController.ParameterIdentifierName.invitationAccepted,
                        "YES"
                    )
                }
                else {
                    loginRoute = loginRoute.routeBySettingParameterInComponent(
                        LoginViewController.routeComponentName,
                        LoginViewController.ParameterIdentifierName.passwordResetSuccess,
                        "YES"
                    )
                }

                try {
                    await RHCore.sharedInstance.logOutAndApplyRoute(loginRoute)
                }
                catch {
                    // The password change has already succeeded. Do not present
                    // it as failed or allow the old account session to win the
                    // next route; complete the logout with a full navigation.
                    window.location.href = "/auth/logout"
                }
                return

            }

            this._handlePasswordSubmissionError(response.errorResult, isInvitationAcceptance)

        }
        catch (error) {

            if (!this._activeRouteMatchesTokens(resetToken, invitationToken)) {
                return
            }

            if (isInvitationAcceptance) {
                this._showError("Viga kutse vastuvõtmisel. Palun proovi hiljem uuesti.")
            }
            else {
                this._showError("Viga salasõna lähtestamisel. Palun proovi hiljem uuesti.")
            }
            this.submitButton.enabled = YES

        }

    }


    _handlePasswordSubmissionError(errorResult: RHSocketErrorResult, isInvitationAcceptance: boolean) {

        const message = ResetPasswordViewController._messageFromSocketErrorResult(errorResult)

        if (
            message === RHUserPasswordPolicy.minimumLengthRequirementMessage() ||
            message === RHUserPasswordPolicy.maximumLengthRequirementMessage() ||
            message === RHUserPasswordPolicy.commonPatternRequirementMessage()
        ) {
            CBDialogViewShower.alert(message)
            this.submitButton.enabled = YES
            return
        }

        if (message === "Kutse vastuvõtmine on juba pooleli. Palun proovige hetke pärast uuesti.") {
            this._showError("Kutse vastuvõtmine on juba pooleli. Palun proovi hetke pärast uuesti.")
            this.submitButton.enabled = YES
            return
        }

        if (
            message === "Kutse vastuvõtmine on juba teise parooliga pooleli. Kasutage sama parooli." ||
            message === "Kutse vastuvõtmine on juba teise salasõnaga pooleli. Kasuta sama salasõna."
        ) {
            this._showError(
                "Kutse vastuvõtmine on juba teise salasõnaga pooleli. Kasuta sama salasõna."
            )
            this.submitButton.enabled = YES
            return
        }

        if (message === ResetPasswordViewController._technicalErrorMessage) {
            if (isInvitationAcceptance) {
                this._showError("Viga kutse vastuvõtmisel. Palun proovi hiljem uuesti.")
            }
            else {
                this._showError("Viga salasõna lähtestamisel. Palun proovi hiljem uuesti.")
            }
            this.submitButton.enabled = YES
            return
        }

        if (isInvitationAcceptance) {
            this._showError("Kutse on aegunud, juba kasutatud või vigane. Palu saata uus kutse.")
        }
        else {
            this._showError("Link on aegunud või vigane. Palun taotle uus link.")
        }
        this.submitButton.enabled = NO

    }


    _activeRouteMatchesTokens(resetToken: string, invitationToken: string) {

        const component = UIRoute.currentRoute.componentWithName(
            ResetPasswordViewController.routeComponentName
        )
        const activeResetToken = component?.parameters[
            ResetPasswordViewController.ParameterIdentifierName.token
        ] ?? ""
        const activeInvitationToken = component?.parameters[
            ResetPasswordViewController.ParameterIdentifierName.invitationToken
        ] ?? ""

        return activeResetToken === resetToken && activeInvitationToken === invitationToken

    }
    
    
    _showError(message: string) {
        
        this.statusBannerView.configureWithObject({
            text: message,
            alertType: RHAlertBannerView.type.Error
        })
        this.view.setNeedsLayoutUpToRootView()
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        const contentBounds = bounds.rectangleWithMaxWidth(RHLoginViewTokens.authContentMaxWidth, 0.5)
        
        this.titleLabel.frame = contentBounds.rectangleWithHeight(this.titleLabel)

        const invitationPreviewBannerHeight = IF(this.invitationPreviewBannerView.text)(() =>
            this.invitationPreviewBannerView.intrinsicContentHeight(contentBounds.width)
        ).ELSE(() =>
            0
        )
        this.invitationPreviewBannerView.frame = this.titleLabel.frame.rectangleForNextRow(
            IF(this.invitationPreviewBannerView.text)(() => padding).ELSE(() => 0),
            invitationPreviewBannerHeight
        )
        
        const bannerHeight = IF(this.statusBannerView.text)(() =>
            this.statusBannerView.intrinsicContentHeight(contentBounds.width)
        ).ELSE(() =>
            0
        )
        this.statusBannerView.frame = this.invitationPreviewBannerView.frame.rectangleForNextRow(
            IF(this.statusBannerView.text)(() => padding).ELSE(() => 0),
            bannerHeight
        )
        
        this.statusBannerView.frame
            .IF(this.view.hasSubview(this.nameInputView))
            .rectangleForNextRow(padding, this.nameInputView)
            .assignedAsFrameOfView(this.nameInputView)
            .ENDIF()
            .rectangleForNextRow(
            IF(this.statusBannerView.text || this.invitationPreviewBannerView.text)(() =>
                padding
            ).ELSE(() =>
                RHSpacing.medium
            ),
            [
                this.newPasswordTextField.intrinsicContentHeight(contentBounds.width),
                RHForgotPasswordViewTokens.fieldMinHeight
            ].max()
            )
            .assignedAsFrameOfView(this.newPasswordTextField)
        
        this.confirmPasswordTextField.frame = this.newPasswordTextField.frame.rectangleForNextRow(
            padding,
            [
                this.confirmPasswordTextField.intrinsicContentHeight(contentBounds.width),
                RHForgotPasswordViewTokens.fieldMinHeight
            ].max()
        )
        
        const submitButtonWidth = [
            [
                this.submitButton.titleLabel.intrinsicContentWidth(RHForgotPasswordViewTokens.submitButtonHeight) +
                RHButtons.contentPadding * 2 +
                RHSpacing.xLarge,
                RHForgotPasswordViewTokens.submitButtonMinWidth
            ].max(),
            RHForgotPasswordViewTokens.submitButtonMaxWidth,
            contentBounds.width
        ].min()

        const submitButtonRowFrame = this.confirmPasswordTextField.frame.rectangleForNextRow(
            padding,
            RHForgotPasswordViewTokens.submitButtonHeight
        )
        this.submitButton.frame = submitButtonRowFrame.rectangleWithWidth(submitButtonWidth, 0.5)
        
        this.backToLoginButton.frame = submitButtonRowFrame.rectangleForNextRow(
            padding,
            RHForgotPasswordViewTokens.submitButtonHeight
        )
        
        this.bottomView.frame = this.backToLoginButton.frame.rectangleForNextRow(padding, 1)
        
    }
    
}
